import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Lightning } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function SetPassword() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const forced = !!user?.must_change_password;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.next !== form.confirm) { setError("New passwords do not match"); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: form.current, new_password: form.next });
      toast.success("Password updated");
      window.location.href = "/dashboard";
    } catch (err) { setError(fmtErr(err)); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[var(--c-bg)] flex items-center justify-center p-8" data-testid="set-password-page">
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-2.5 mb-8">
          <div className="w-8 h-8 bg-[var(--c-accent)] rounded-sm flex items-center justify-center">
            <Lightning size={18} weight="fill" color="var(--c-text)" />
          </div>
          <span className="text-lg font-semibold text-[var(--c-text)]">Formigen</span>
        </div>
        <h1 className="text-2xl font-medium tracking-tight text-[var(--c-text)]">
          {forced ? "Set your password" : "Change password"}
        </h1>
        <p className="text-xs text-[var(--c-muted)] mt-2 leading-relaxed">
          {forced
            ? "Your administrator created a temporary password for you. Choose your own password to continue."
            : "Enter your current password and choose a new one."}
        </p>
        <form onSubmit={submit} className="space-y-4 mt-6" data-testid="set-password-form">
          <div className="space-y-1.5">
            <Label className="text-[var(--c-muted)] text-xs">{forced ? "Temporary password" : "Current password"}</Label>
            <Input data-testid="sp-current" type="password" required value={form.current}
              onChange={(e) => setForm({ ...form, current: e.target.value })}
              className="bg-[var(--c-surface)] border-[var(--c-border)] text-[var(--c-text)]" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[var(--c-muted)] text-xs">New password</Label>
            <Input data-testid="sp-new" type="password" required minLength={8} value={form.next}
              onChange={(e) => setForm({ ...form, next: e.target.value })}
              className="bg-[var(--c-surface)] border-[var(--c-border)] text-[var(--c-text)]" placeholder="Min. 8 characters" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[var(--c-muted)] text-xs">Confirm new password</Label>
            <Input data-testid="sp-confirm" type="password" required minLength={8} value={form.confirm}
              onChange={(e) => setForm({ ...form, confirm: e.target.value })}
              className="bg-[var(--c-surface)] border-[var(--c-border)] text-[var(--c-text)]" />
          </div>
          {error && <div data-testid="sp-error" className="text-xs text-[var(--c-red)] border border-[var(--c-red)]/40 bg-[var(--c-red)]/10 rounded-sm px-3 py-2">{error}</div>}
          <Button data-testid="sp-submit" disabled={busy}
            className="w-full bg-[var(--c-accent)] hover:bg-[var(--c-accent-hover)] text-white rounded-sm">
            {busy ? "Saving…" : "Save password"}
          </Button>
          {!forced && (
            <button type="button" onClick={() => nav(-1)} data-testid="sp-cancel"
              className="w-full text-center text-xs text-[var(--c-muted)] hover:text-[var(--c-text)] transition-colors duration-150">
              Cancel
            </button>
          )}
        </form>
      </div>
    </div>
  );
}
